import { useMemo } from 'react';

import { useConfigurationStore } from 'store/configurationsStore';

import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import { AutomatedNarrativeParams } from 'components/Dashboards/Reporting/threeDotsMenu/hooks/useAddAutomatedNarrativeWidgetToReport';

interface Params {
    reportWidgets: Widget[] | undefined;
}

export type NarrativeWidgetsRelationsMapType = Map<number, Set<number>>;

/**
 * Build two maps describing relations between widgets and their Automated Narrative widgets.
 *
 * Why:
 *  - We need a fast, constant-time way to answer questions like:
 *      • "For given any widgetId is there any relations"
 *      • "For given any widgetId is there are children (Narrative widget)"
 *
 * What is built:
 *  1) `widgetToChildNarrativeMap` — a *directed* map: parent → Set(children narrative widgets)
 *     Used when you want to delete widget from report and want to check if there are children(narrative).
 *
 *  2) `narrativeWidgetsRelationsMap` — a *bidirectional* map: for every related widget returns
 *     a Set including both itself and its counterpart(s). Used mainly for hover highlighting.
 *
 * Example:
 *  widgets = [1,2,3,4,5], and widget 3 is AutomatedNarrative of widget 2
 *
 *  widgetToChildNarrativeMap:
 *      2 -> {3}
 *
 *  narrativeWidgetsRelationsMap:
 *      2 -> {2,3}
 *      3 -> {2,3}
 *
 * Complexity:
 *  O(n) build time, O(1) relation lookup
 */

export const useNarrativeMaps = ({ reportWidgets }: Params) => {
    const configurationStore = useConfigurationStore();

    return useMemo(() => {
        const widgets = reportWidgets ?? [];
        const widgetIds = new Set(widgets.map((widget) => widget.id));

        const narrativeWidgetsRelationsMap = new Map<number, Set<number>>();
        const widgetToChildNarrativeMap = new Map<number, Set<number>>();

        for (const widget of widgets) {
            if (widget.componentKey === configurationStore.automatedNarrative.componentKey) {
                let params: AutomatedNarrativeParams | null = null;
                try {
                    params = typeof widget.params === 'string' ? JSON.parse(widget.params) : widget.params;
                } catch {
                    // ignore invalid JSON
                }

                const parentId = params?.parentWidgetId;
                const childId = widget.id;
                const existsInList = typeof parentId === 'number' && widgetIds.has(parentId);

                if (existsInList) {
                    // widgetToChildNarrativeMap (directed: parent -> child)
                    if (!widgetToChildNarrativeMap.has(parentId)) {
                        widgetToChildNarrativeMap.set(parentId, new Set());
                    }
                    widgetToChildNarrativeMap.get(parentId)?.add(childId);

                    // === relationsMap (bidirectional for hover) ===
                    if (!narrativeWidgetsRelationsMap.has(childId)) {
                        narrativeWidgetsRelationsMap.set(childId, new Set());
                    }
                    if (!narrativeWidgetsRelationsMap.has(parentId)) {
                        narrativeWidgetsRelationsMap.set(parentId, new Set());
                    }
                    narrativeWidgetsRelationsMap.get(childId)?.add(childId).add(parentId);
                    narrativeWidgetsRelationsMap.get(parentId)?.add(parentId).add(childId);
                }
            }
        }

        return { narrativeWidgetsRelationsMap, widgetToChildNarrativeMap };
    }, [reportWidgets]);
};
